import { useRequiredContext } from "@redotech/react-util/context";
import { useRpcData } from "@redotech/react-util/rpc-data";
import PencilIcon from "@redotech/redo-icons/pencil-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { ExternalReturnHubType } from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTable } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTablePaddedCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { InputProvider } from "@redotech/ui/form";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import type { availableReturnsCentersInput } from "./redo-managed-returns-settings";

export interface ReturnsCenterConfigFormValue {
  returnsCenterId: string;
  consolidationScheduleDays: string;
  shipToName: string;
  shipToStreet1: string;
  shipToStreet2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  refulfillment: boolean;
}

type AvailableReturnsCentersForm = InputProvider.Form<
  typeof availableReturnsCentersInput
>;

interface ReturnsCenterRowData {
  id: string;
  label: string;
  schedule: string;
  addressSummary: string;
  provider: string;
  locationId: string;
  refulfillment: boolean;
  hasLinkedLocation: boolean;
}

const HUB_DISPLAY_NAMES: Record<ExternalReturnHubType, string> = {
  [ExternalReturnHubType.RETURNBEAR]: "ReturnBear",
  [ExternalReturnHubType.BLUEYONDER]: "Blue Yonder",
  [ExternalReturnHubType.SHIPMONK]: "ShipMonk",
  [ExternalReturnHubType.RYDER]: "Ryder",
  [ExternalReturnHubType.DEPOSCO]: "Deposco",
};

const EXTERNAL_HUB_OPTIONS: RedoListItem<string>[] = [
  { id: "", type: "text" as const, text: "None", value: "" },
  ...Object.values(ExternalReturnHubType).map((hub) => ({
    id: hub,
    type: "text" as const,
    text: HUB_DISPLAY_NAMES[hub],
    value: hub,
  })),
];

function summarizeAddress(config: ReturnsCenterConfigFormValue): string {
  const parts = [config.shipToCity, config.shipToState, config.shipToCountry]
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
}

function formatSchedule(days: string): string {
  const trimmed = days.trim();
  if (!trimmed) return "Not scheduled";
  const n = parseInt(trimmed, 10);
  if (isNaN(n)) return trimmed;
  if (n === 1) return "Every day";
  return `Every ${n} days`;
}

const EMPTY_CONFIG: Omit<ReturnsCenterConfigFormValue, "returnsCenterId"> = {
  consolidationScheduleDays: "",
  shipToName: "",
  shipToStreet1: "",
  shipToStreet2: "",
  shipToCity: "",
  shipToState: "",
  shipToZip: "",
  shipToCountry: "",
  refulfillment: false,
};

function centerLabel(center: {
  name: string;
  city: string | null;
  state: string | null;
}): string {
  return `${center.name}${center.city ? ` - ${center.city}` : ""}${center.state ? `, ${center.state}` : ""}`;
}

function buildColumns(
  onEdit: (centerId: string) => void,
  onRemove: (centerId: string) => void,
  onToggleRefulfillment: (centerId: string, value: boolean) => void,
  disabled: boolean,
) {
  return [
    {
      key: "name",
      widthMode: { type: "fill" as const, relativeLength: 3 },
      alignment: "left" as const,
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Returns Center" />
      ),
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          <Text fontSize="xs" fontWeight="medium">
            {row.label}
          </Text>
        </RedoTablePaddedCell>
      ),
    },
    {
      key: "schedule",
      widthMode: { type: "fill" as const, relativeLength: 2 },
      alignment: "left" as const,
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Schedule" />
      ),
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          <Text fontSize="xs" textColor="secondary">
            {row.schedule}
          </Text>
        </RedoTablePaddedCell>
      ),
    },
    {
      key: "shipTo",
      widthMode: { type: "fill" as const, relativeLength: 2 },
      alignment: "left" as const,
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Ship-To" />
      ),
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          <Text fontSize="xs" textColor="secondary">
            {row.addressSummary || "\u2014"}
          </Text>
        </RedoTablePaddedCell>
      ),
    },
    {
      key: "provider",
      widthMode: { type: "fill" as const, relativeLength: 2 },
      alignment: "left" as const,
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Provider" />
      ),
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          <Text fontSize="xs" textColor="secondary">
            {row.provider
              ? (HUB_DISPLAY_NAMES[row.provider as ExternalReturnHubType] ??
                row.provider)
              : "\u2014"}
          </Text>
        </RedoTablePaddedCell>
      ),
    },
    {
      key: "locationId",
      widthMode: { type: "fill" as const, relativeLength: 2 },
      alignment: "left" as const,
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Location ID" />
      ),
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          <Text fontSize="xs" textColor="secondary">
            {row.provider && row.locationId ? row.locationId : "\u2014"}
          </Text>
        </RedoTablePaddedCell>
      ),
    },
    {
      key: "refulfillment",
      widthMode: { type: "hug" as const },
      alignment: "center" as const,
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Re-fulfillment" />
      ),
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          {row.hasLinkedLocation && (
            <RedoToggle
              disabled={disabled}
              setValue={(value) => onToggleRefulfillment(row.id, value)}
              size="sm"
              value={row.refulfillment}
            />
          )}
        </RedoTablePaddedCell>
      ),
    },
    {
      key: "actions",
      widthMode: { type: "hug" as const },
      alignment: "right" as const,
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }: { row: ReturnsCenterRowData }) => (
        <RedoTablePaddedCell>
          <Flex dir="row" gap="xs">
            <RedoButton
              disabled={disabled}
              hierarchy="tertiary"
              IconLeading={PencilIcon}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row.id);
              }}
              size="xs"
            />
            <RedoButton
              disabled={disabled}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(row.id);
              }}
              size="xs"
            />
          </Flex>
        </RedoTablePaddedCell>
      ),
    },
  ];
}

export const AvailableReturnsCentersTable = memo(
  function AvailableReturnsCentersTable({
    input,
    disabled,
    hubMappings,
    onHubMappingsChange,
  }: {
    input: AvailableReturnsCentersForm;
    disabled: boolean;
    hubMappings: Record<string, { provider: string; locationId: string }>;
    onHubMappingsChange: (
      mappings: Record<string, { provider: string; locationId: string }>,
    ) => void;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

    const { value: returnsCentersData, pending: returnsCentersPending } =
      useRpcData(rpcClient, "getReturnsCenters", { input: { sort: null } });
    const allReturnsCenters = useMemo(
      () => returnsCentersData?.returnsCenters ?? [],
      [returnsCentersData],
    );

    const configs = input.value;

    const [editingCenterId, setEditingCenterId] = useState<string | null>(null);
    const [editDraft, setEditDraft] =
      useState<ReturnsCenterConfigFormValue | null>(null);
    const [editHubProvider, setEditHubProvider] = useState("");
    const [editHubLocationId, setEditHubLocationId] = useState("");

    const openEditModal = useCallback(
      (centerId: string) => {
        const existing = configs.find((c) => c.returnsCenterId === centerId);
        setEditDraft(
          existing ?? { returnsCenterId: centerId, ...EMPTY_CONFIG },
        );
        const mapping = hubMappings[centerId];
        setEditHubProvider(mapping?.provider ?? "");
        setEditHubLocationId(mapping?.locationId ?? "");
        setEditingCenterId(centerId);
      },
      [configs, hubMappings],
    );

    const closeEditModal = useCallback(() => {
      setEditingCenterId(null);
      setEditDraft(null);
      setEditHubProvider("");
      setEditHubLocationId("");
    }, []);

    const saveEditModal = useCallback(() => {
      if (!editDraft) return;
      input.setValue(
        configs.map((c) =>
          c.returnsCenterId === editDraft.returnsCenterId ? editDraft : c,
        ),
      );
      const centerId = editDraft.returnsCenterId;
      const newMappings = { ...hubMappings };
      if (!editHubProvider) {
        delete newMappings[centerId];
      } else {
        newMappings[centerId] = {
          provider: editHubProvider,
          locationId: editHubLocationId,
        };
      }
      onHubMappingsChange(newMappings);
      closeEditModal();
    }, [
      editDraft,
      configs,
      input,
      hubMappings,
      editHubProvider,
      editHubLocationId,
      onHubMappingsChange,
      closeEditModal,
    ]);

    const removeCenter = useCallback(
      (centerId: string) => {
        input.setValue(configs.filter((c) => c.returnsCenterId !== centerId));
      },
      [configs, input],
    );

    const toggleRefulfillment = useCallback(
      (centerId: string, value: boolean) => {
        input.setValue(
          configs.map((c) =>
            c.returnsCenterId === centerId ? { ...c, refulfillment: value } : c,
          ),
        );
      },
      [configs, input],
    );

    const addCenter = useCallback(
      (centerId: string) => {
        input.setValue([
          ...configs,
          { returnsCenterId: centerId, ...EMPTY_CONFIG },
        ]);
      },
      [configs, input],
    );

    const columns = useMemo(
      () =>
        buildColumns(
          openEditModal,
          removeCenter,
          toggleRefulfillment,
          disabled,
        ),
      [openEditModal, removeCenter, toggleRefulfillment, disabled],
    );

    const layout = useMemo(
      () => ({ columns, headerColor: "grey" as const }),
      [columns],
    );

    const selectedCenterIds = useMemo(
      () => new Set(configs.map((c) => c.returnsCenterId)),
      [configs],
    );

    const rowsOfData: ReturnsCenterRowData[] = useMemo(() => {
      return configs.map((config) => {
        const center = allReturnsCenters.find(
          (c) => c.id === config.returnsCenterId,
        );
        const mapping = hubMappings[config.returnsCenterId];
        return {
          id: config.returnsCenterId,
          label: center ? centerLabel(center) : config.returnsCenterId,
          schedule: formatSchedule(config.consolidationScheduleDays),
          addressSummary: summarizeAddress(config),
          provider: mapping?.provider ?? "",
          locationId: mapping?.locationId ?? "",
          refulfillment: config.refulfillment,
          hasLinkedLocation: center?.hasLinkedLocation ?? false,
        };
      });
    }, [allReturnsCenters, configs, hubMappings]);

    const addOptions: RedoListItem<string>[] = useMemo(() => {
      return allReturnsCenters
        .filter((c) => !selectedCenterIds.has(c.id))
        .map((center) => ({
          type: "text" as const,
          id: center.id,
          value: center.id,
          text: centerLabel(center),
        }));
    }, [allReturnsCenters, selectedCenterIds]);

    const handleAddSelected = useCallback(
      ({ item }: { item: RedoListItem<string> }) => {
        addCenter(item.value);
      },
      [addCenter],
    );

    if (returnsCentersPending) {
      return (
        <Text fontSize="sm" textColor="tertiary">
          Loading returns centers...
        </Text>
      );
    }

    const editingCenter = editingCenterId
      ? allReturnsCenters.find((c) => c.id === editingCenterId)
      : null;

    const hasAvailableCenters = addOptions.length > 0;

    return (
      <Flex dir="column" gap="md">
        {hasAvailableCenters && (
          <RedoSingleSelectDropdownInput
            disabled={disabled}
            options={addOptions}
            optionSelected={handleAddSelected}
            placeholder="Add a returns center..."
            size="xs"
          />
        )}
        {rowsOfData.length > 0 && (
          <Flex
            style={{
              border: "1px solid var(--redo-colors-border-border-primary)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <div style={{ margin: "-1px 0", width: "100%" }}>
              <RedoTable layout={layout} rowsOfData={rowsOfData} />
            </div>
          </Flex>
        )}

        {editDraft && editingCenter && (
          <EditReturnsCenterModal
            centerLabel={centerLabel(editingCenter)}
            draft={editDraft}
            hubLocationId={editHubLocationId}
            hubProvider={editHubProvider}
            onClose={closeEditModal}
            onHubLocationIdChange={setEditHubLocationId}
            onHubProviderChange={setEditHubProvider}
            onSave={saveEditModal}
            setDraft={setEditDraft}
          />
        )}
      </Flex>
    );
  },
);

const EditReturnsCenterModal = memo(function EditReturnsCenterModal({
  centerLabel,
  draft,
  setDraft,
  hubProvider,
  hubLocationId,
  onHubProviderChange,
  onHubLocationIdChange,
  onClose,
  onSave,
}: {
  centerLabel: string;
  draft: ReturnsCenterConfigFormValue;
  setDraft: (value: ReturnsCenterConfigFormValue) => void;
  hubProvider: string;
  hubLocationId: string;
  onHubProviderChange: (provider: string) => void;
  onHubLocationIdChange: (locationId: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const updateField = useCallback(
    (field: keyof ReturnsCenterConfigFormValue, value: string) => {
      setDraft({ ...draft, [field]: value });
    },
    [draft, setDraft],
  );

  const selectedProviderOption = useMemo(
    () =>
      EXTERNAL_HUB_OPTIONS.find((o) => o.value === hubProvider) ??
      EXTERNAL_HUB_OPTIONS[0],
    [hubProvider],
  );

  const handleProviderSelected = useCallback(
    ({ item }: { item: RedoListItem<string> }) => {
      onHubProviderChange(item.value);
      if (!item.value) {
        onHubLocationIdChange("");
      }
    },
    [onHubProviderChange, onHubLocationIdChange],
  );

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Save",
        onClick: onSave,
        disabled: false,
        size: "xs",
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
        disabled: false,
        size: "xs",
      }}
      subtitle="Configure consolidation, ship-to address, and hub provider."
      title={centerLabel}
    >
      <Flex dir="column" gap="lg">
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Consolidation
          </Text>
          <RedoTextInput
            label="Days between shipments"
            placeholder="e.g. 7"
            setValue={(v) => updateField("consolidationScheduleDays", v)}
            value={draft.consolidationScheduleDays}
          />
        </Flex>
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Ship-To Address
          </Text>
          <RedoTextInput
            label="Name"
            setValue={(v) => updateField("shipToName", v)}
            value={draft.shipToName}
          />
          <RedoTextInput
            label="Street 1"
            setValue={(v) => updateField("shipToStreet1", v)}
            value={draft.shipToStreet1}
          />
          <RedoTextInput
            label="Street 2"
            setValue={(v) => updateField("shipToStreet2", v)}
            value={draft.shipToStreet2}
          />
          <Flex dir="row" gap="sm" w="full">
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <RedoTextInput
                label="City"
                setValue={(v) => updateField("shipToCity", v)}
                value={draft.shipToCity}
              />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <RedoTextInput
                label="State"
                setValue={(v) => updateField("shipToState", v)}
                value={draft.shipToState}
              />
            </div>
          </Flex>
          <Flex dir="row" gap="sm" w="full">
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <RedoTextInput
                label="ZIP"
                setValue={(v) => updateField("shipToZip", v)}
                value={draft.shipToZip}
              />
            </div>
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <RedoTextInput
                label="Country"
                setValue={(v) => updateField("shipToCountry", v)}
                value={draft.shipToCountry}
              />
            </div>
          </Flex>
        </Flex>
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            External Hub
          </Text>
          <RedoSingleSelectDropdownInput
            label="Provider"
            options={EXTERNAL_HUB_OPTIONS}
            optionSelected={handleProviderSelected}
            selectedItem={selectedProviderOption}
            size="xs"
          />
          {hubProvider && (
            <RedoTextInput
              label="Location ID"
              placeholder="Enter location ID"
              setValue={onHubLocationIdChange}
              value={hubLocationId}
            />
          )}
        </Flex>
      </Flex>
    </RedoModal>
  );
});
